import React from 'react';
import styled from 'styled-components';
import { Flame } from 'lucide-react';
import { theme } from '../styles/theme';
import { Badge } from '../styles/GlobalStyles';

const ItemCard = styled.div`
  background: ${theme.colors.glass.background};
  backdrop-filter: ${theme.colors.glass.backdrop};
  border: 1px solid ${theme.colors.glass.border};
  border-radius: 20px;
  margin-bottom: ${theme.spacing.md};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  box-shadow: ${theme.colors.glass.shadow};
  
  /* 🚀 MOBİL SCROLL OPTİMİZASYONU */
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  /* Mobilde will-change'i kaldır, sadece hover'da ekle */
  will-change: auto;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    border-radius: 16px;
    margin-bottom: ${theme.spacing.sm};
  }
  
  /* 🎨 SOFISTIKE BORDER EFEKTİ */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: 1px;
    background: ${theme.colors.gradient.primary};
    border-radius: 20px;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: 0;
  }
  
  /* ✨ ZARIF İÇ EFEKTİ */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${theme.colors.gradient.elegant};
    opacity: 0;
    transition: all 0.4s ease;
    z-index: 0;
    border-radius: 19px;
  }
  
  /* Mobil cihazlarda hover efektlerini devre dışı bırak */
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translate3d(0, -8px, 0) scale(1.02);
      box-shadow: 
        0 25px 50px -12px rgba(99, 102, 241, 0.3),
        0 0 40px rgba(139, 92, 246, 0.2),
        inset 0 1px 0 rgba(248, 250, 252, 0.1);
      border-color: ${theme.colors.glass.borderGlow};
      will-change: transform, box-shadow;
      
      &::before {
        opacity: 0.8;
      }
      
      &::after {
        opacity: 0.05;
      }
    }
  }
  
  /* Mobil cihazlarda touch feedback */
  @media (max-width: 767px) {
    &:active {
      transform: translate3d(0, -2px, 0) scale(0.98);
      transition: transform 0.1s ease;
    }
  }
  
  & > * {
    position: relative;
    z-index: 1;
  }
`;

const ItemContent = styled.div`
  padding: ${theme.spacing.md};
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xs};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm};
    gap: ${theme.spacing.xs};
  }
`;

const ItemHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr; /* Sol: isim, Orta: badge, Sağ: fiyat (sağa yaslı) */
  align-items: center;
  gap: ${theme.spacing.sm};
  width: 100%;
`;

const ItemName = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
  line-height: 1.4;
  letter-spacing: -0.01em;
  flex: 1;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  grid-column: 1;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.fontSizes.md};
  }
`;

const ItemPrice = styled.div`
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  color: ${theme.colors.text.white};
  white-space: nowrap;
  background: ${theme.colors.gradient.accent};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: 50px;
  box-shadow: 
    0 4px 20px rgba(245, 158, 11, 0.3),
    0 0 30px rgba(139, 92, 246, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  position: relative;
  min-width: 60px;
  text-align: center;
  text-shadow: 0 2px 8px rgba(15, 23, 42, 0.6);
  overflow: hidden;
  justify-self: end; /* Sağ sütunda sağa yasla */
  grid-column: 3;
  
  /* 🎨 SOFISTIKE BORDER */
  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    background: ${theme.colors.gradient.primary};
    border-radius: 50px;
    z-index: -1;
    opacity: 0;
    transition: all 0.3s ease;
    filter: blur(2px);
  }
  
  /* ✨ ELEGANT SHIMMER */
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent 40%, rgba(248, 250, 252, 0.2) 50%, transparent 60%);
    transform: rotate(45deg);
    animation: elegantShimmer 4s ease-in-out infinite;
  }
  
  ${ItemCard}:hover & {
    transform: scale(1.05);
    box-shadow: 
      0 6px 30px rgba(245, 158, 11, 0.4),
      0 0 40px rgba(139, 92, 246, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
    
    &::before {
      opacity: 0.6;
      filter: blur(3px);
    }
  }
  
  @keyframes elegantShimmer {
    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); opacity: 0; }
    50% { opacity: 1; }
    100% { transform: translateX(100%) translateY(100%) rotate(45deg); opacity: 0; }
  }
`;

const SpicyBadgeInline = styled(Badge)`
  justify-self: center; /* Tam orta sütun */
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  font-size: ${theme.fontSizes.sm};
  line-height: 1;
  grid-column: 2;
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 280px;
  background: ${theme.colors.gradient.elegant};
  position: relative;
  overflow: hidden;
  display: block;
  border-radius: 18px 18px 0 0;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    height: 300px; /* Üst alanı büyüt */
    border-radius: 14px 14px 0 0;
  }

  /* İçecek konteyneri için karışımı izole et */
  &[data-category='beverages'] {
    isolation: isolate;
  }
`;

const UniformImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
  /* Mobil optimizasyon */
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  will-change: auto;

  /* İçecek kategorisi için orijinal oranı koru */
  &[data-category='beverages'] {
    object-fit: contain;
    padding: ${theme.spacing.xs};
    background: transparent;
  }
  
  /* Sadece masaüstünde hover efekti */
  @media (hover: hover) and (pointer: fine) {
    ${ItemCard}:hover & {
      transform: translate3d(0, 0, 0) scale(1.03);
      will-change: transform;
    }
  }
  
  /* Kaşarlı cantık rotasyonu kaldırıldı - düz görünüm */
`;

export const MenuItem = ({ item, categoryId }) => {
  return (
    <ItemCard>
      {item.image && (
        <ImageContainer data-category={categoryId}>
          <UniformImage 
            src={item.image} 
            alt={item.name}
            itemId={item.id}
            data-category={categoryId}
            loading="lazy"
            decoding="async"
            fetchpriority="low"
            onError={(e) => {
              // Fotoğraf bulunamazsa container'ı gizle
              e.target.parentElement.style.display = 'none';
            }}
            onLoad={(e) => {
              // Görsel yüklendikten sonra will-change'i kaldır
              e.target.style.willChange = 'auto';
            }}
          />
        </ImageContainer>
      )}
      
      <ItemContent>
        <ItemHeader>
          <ItemName>{item.name}</ItemName>
          {item.isSpicy && (
            <SpicyBadgeInline variant="spicy">
              <Flame size={16} />
              Acılı
            </SpicyBadgeInline>
          )}
          <ItemPrice>₺{item.price}</ItemPrice>
        </ItemHeader>
      </ItemContent>
    </ItemCard>
  );
};
